import type { ReactNode } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Kicker } from "./Kicker";
import { fadeIn } from "./fadeIn";
import { AskStepMock, HostStepMock, SearchStepMock, WatchStepMock } from "./HowMocks";
import { DOCUMENTED_INTEGRATION_COUNT } from "@/data/integrationCount";

/**
 * How Huntbase gets you there: the four steps, named the same way everywhere
 * (site, decks, videos). One sentence and one focused mock each.
 */
/** `mobile`: also show the mock on phones. One of four keeps the section to a few screens. */
const STEPS: Array<{
  name: string;
  text: string;
  note?: string;
  visual: ReactNode;
  mobile?: boolean;
}> = [
  {
    name: "Ask",
    text: "Start from a question, an advisory or a hunch. Scout plans the hunt and writes the queries in each tool's own language.",
    visual: <AskStepMock />,
  },
  {
    name: "Search the tools you already run",
    text: `One hunt across your SIEMs, EDRs, clouds and data lakes through ${DOCUMENTED_INTEGRATION_COUNT} integrations, without moving data first. The Huntbase Lake holds anything you choose to keep with us.`,
    visual: <SearchStepMock />,
  },
  {
    name: "Ask the host",
    text: "When the answer was never logged, the endpoint agent asks the host directly. Actions such as isolating a host run as signed scripts and, by default, only once a second person approves.",
    note: "Endpoint agent and response actions: available on request.",
    visual: <HostStepMock />,
    mobile: true,
  },
  {
    name: "Keep watching",
    text: "Turn the hunt into a watcher. Watchers are backtested and run in shadow before they go live. When one fires, Scout pre-reads the detection into an insight.",
    note: "Backtesting and shadow mode: available on request.",
    visual: <WatchStepMock />,
  },
];

export function HowSection() {
  const reduce = useReducedMotion();
  return (
    <section className="bg-paper px-6 py-10 md:px-12 md:py-28" aria-labelledby="how-heading">
      <div className="mx-auto flex max-w-7xl flex-col gap-10 md:gap-14">
        <motion.div {...fadeIn(reduce)} className="flex max-w-3xl flex-col gap-4 md:gap-5">
          <Kicker>How Huntbase gets you there</Kicker>
          <h2 id="how-heading" className="heading-lg text-ink text-balance">
            Four steps from question to evidence, including what was never logged.
          </h2>
          <p className="hidden text-lg leading-relaxed text-ink-muted md:block">
            Scout does the legwork at every step. Your team approves what matters, and every step
            leaves evidence and a hunt report you can hand to your CISO, auditor or client.
          </p>
        </motion.div>

        <ol className="grid gap-8 md:grid-cols-2 md:gap-x-10 md:gap-y-14">
          {STEPS.map((s, i) => (
            <motion.li key={s.name} {...fadeIn(reduce, 0.05 * i)} className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <h3 className="heading-sm flex items-baseline gap-3 text-ink">
                  <span className="text-primary-500" aria-hidden>
                    {i + 1}
                  </span>
                  {s.name}
                </h3>
                <p className="text-[15px] leading-relaxed text-ink-muted md:text-base">{s.text}</p>
                {s.note && <p className="text-xs text-ink-subtle">{s.note}</p>}
              </div>
              <div className={s.mobile ? "md:mt-auto" : "hidden md:mt-auto md:block"}>
                {s.visual}
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}
